import { apiDelete, apiGet } from "./apiClient";

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray(value.items)) return value.items;
  if (value && Array.isArray(value.data)) return value.data;
  return [];
}

/** Normalizes API row to camelCase CustomerDto shape. */
export function normalizeCustomer(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  return {
    id,
    name: row.name ?? row.Name ?? "",
    email: row.email ?? row.Email ?? "",
    phoneNo: row.phoneNo ?? row.PhoneNo ?? "",
    dob: row.dob ?? row.DOB ?? null,
    photo: row.photo ?? row.Photo ?? "",
    gender: row.gender ?? row.Gender ?? "",
    isEmailVerified: Boolean(row.isEmailVerified ?? row.IsEmailVerified),
    isPhoneNoVerifies: Boolean(row.isPhoneNoVerifies ?? row.IsPhoneNoVerifies),
    status: row.status ?? row.Status ?? "Active",
    lastLogin: row.lastLogin ?? row.LastLogin ?? null,
    createdDate: row.createdDate ?? row.CreatedDate ?? null,
    updatedDate: row.updatedDate ?? row.UpdatedDate ?? null,
    whatsAppNo: row.whatsAppNo ?? row.WhatsAppNo ?? ""
  };
}

/** Normalizes API row to camelCase CustomerAddressDto shape. */
export function normalizeCustomerAddress(row) {
  if (!row || typeof row !== "object") return null;
  const id = row.id ?? row.Id;
  if (id == null) return null;
  const customerId = row.customerId ?? row.CustomerId;
  return {
    id,
    customerId,
    address: row.address ?? row.Address ?? "",
    addressType: row.addressType ?? row.AddressType ?? "",
    countryId: row.countryId ?? row.CountryId,
    stateId: row.stateId ?? row.StateId,
    cityId: row.cityId ?? row.CityId,
    pobox: row.pobox ?? row.POBOX ?? row.Pobox ?? "",
    contactPersonName: row.contactPersonName ?? row.ContactPersonName ?? "",
    contactPersonMob: row.contactPersonMob ?? row.ContactPersonMob ?? "",
    isDefault: Boolean(row.isDefault ?? row.IsDefault)
  };
}

function normalizeList(rows, normalizer) {
  return asArray(rows)
    .map(normalizer)
    .filter(Boolean);
}

async function getCollection(path, errorMessage, normalizer) {
  const res = await apiGet(path);
  if (!res.ok) return { ok: false, error: res.error ?? errorMessage, items: [] };
  const items = normalizer ? normalizeList(res.data, normalizer) : asArray(res.data);
  return { ok: true, items };
}

export function fetchWalletsByCustomerId(customerId) {
  return getCollection(`/api/Wallets/by-customer/${customerId}`, "Failed to load wallet");
}

export function fetchWalletTransactionsByCustomerId(customerId) {
  return getCollection(`/api/WalletTransactions/by-customer/${customerId}`, "Failed to load wallet transactions");
}

export function fetchWishlistsByCustomerId(customerId) {
  return getCollection(`/api/Wishlists/by-customer/${customerId}`, "Failed to load wishlists");
}

export function fetchCartsByCustomerId(customerId) {
  return getCollection(`/api/Carts/by-customer/${customerId}`, "Failed to load cart items");
}

export function fetchWalletTransactionsList() {
  return getCollection("/api/WalletTransactions", "Failed to load wallet transactions");
}

export async function fetchCustomersList() {
  return getCollection("/api/Customers", "Failed to load customers", normalizeCustomer);
}

export async function fetchCustomerById(customerId) {
  const res = await apiGet(`/api/Customers/${customerId}`);
  if (!res.ok) return { ok: false, error: res.error ?? "Failed to load customer", customer: null };
  return { ok: true, customer: normalizeCustomer(res.data) };
}

/** Loads customers for order/cart joins, fetching any inactive/missing IDs individually. */
export async function fetchCustomersForLookup(orderCustomerIds = []) {
  const listRes = await fetchCustomersList();
  if (!listRes.ok) return listRes;

  const byId = new Map();
  for (const c of listRes.items) byId.set(Number(c.id), c);

  const missingIds = [...new Set(orderCustomerIds.map(Number).filter((id) => Number.isFinite(id) && !byId.has(id)))];
  if (missingIds.length > 0) {
    const detailResults = await Promise.all(missingIds.map((id) => fetchCustomerById(id)));
    for (const res of detailResults) {
      if (res.ok && res.customer) byId.set(Number(res.customer.id), res.customer);
    }
  }

  return { ok: true, items: [...byId.values()] };
}

export function deleteCustomer(id) {
  return apiDelete(`/api/Customers/${id}`);
}

export function fetchCustomerAddressesList() {
  return getCollection("/api/CustomerAddresses", "Failed to load customer addresses", normalizeCustomerAddress);
}

export function deleteCustomerAddress(id) {
  return apiDelete(`/api/CustomerAddresses/${id}`);
}
